import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown, Pencil } from "lucide-react";
import api, { errText } from "@/lib/api";
import { RichText } from "@/components/RichText";

const isRich = (s) => /<\/?(strong|b|em|i|u|a|p|br|div)\b/i.test(s || "");
const escapeHtml = (s) =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\{image\}/g, "{image}");

// Older wording saved as paragraph blocks previews as the same single flow of lines the editor
// and the sent email use.
const flatten = (html) =>
  (html || "")
    .replace(/<br\s*\/?>/gi, "<br>")
    .replace(/<br>\s*(<\/(?:div|p)>)/gi, "$1")
    .replace(/<\/(?:div|p)>\s*/gi, "<br>")
    .replace(/<(?:div|p)\b[^>]*>/gi, "")
    .replace(/(?:<br>\s*)+$/i, "");

const KINDS = [
  ["invite", "Invitation & RSVP"],
  ["notice", "Confirmation"],
  ["ticket", "Pass"],
  ["reminder", "Reminder"],
  ["survey", "Feedback survey"],
];

const BUILT_IN = KINDS.map(([id]) => id);

const LINK_TARGETS = [
  ["rsvp", "RSVP page — reply yes or no"],
  ["pass", "The guest's pass page"],
  ["survey", "Feedback survey form"],
  ["event", "Public event page"],
  ["url", "A web address I type"],
];

const SAMPLE = {
  salutation: "Dr",
  first_name: "Dana",
  last_name: "Lim",
  reference: "7FEABA",
};

const fill = (text, event) =>
  (text || "")
    .replaceAll("{salutation_name}", `${SAMPLE.salutation} ${SAMPLE.last_name}`)
    .replaceAll("{salutation}", SAMPLE.salutation)
    .replaceAll("{first_name}", SAMPLE.first_name)
    .replaceAll("{last_name}", SAMPLE.last_name)
    .replaceAll("{event_name}", event?.name || "")
    .replaceAll("{date}", event?.date || "")
    .replaceAll("{time}", event?.start_time || "")
    .replaceAll("{venue}", event?.venue || "")
    .replaceAll("{reference}", SAMPLE.reference)
    .replaceAll("{table}", "Table 7")
    .replaceAll("{host_org}", event?.host_org || "Ark Media")
    .replaceAll("{email}", "dana.lim@example.com")
    .replaceAll("{link}", "https://…/ticket/…")
    .replaceAll(
      "{qr}",
      '<img src="/manual/qr-sample.png" alt="QR pass" ' +
        'style="width:150px;height:150px;background:#fff;padding:10px;border-radius:10px" />'
    );

export const MessagesTab = ({ eventId, event, onCustomChange }) => {
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("invite");
  const [messages, setMessages] = useState({});
  const [customs, setCustoms] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [banners, setBanners] = useState({});
  const [rsvpPage, setRsvpPage] = useState({});
  const [passPage, setPassPage] = useState({});
  const [followups, setFollowups] = useState({ rsvp_yes_enabled: false, rsvp_yes_kind: "ticket" });
  const [uploading, setUploading] = useState("");

  const uploadBanner = (scope) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(scope);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data: res } = await api.post(`/events/${eventId}/banner`, body, {
        params: scope === "event" ? {} : { kind: scope },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBanners(res.banners);
      toast.success(scope === "event" ? "Event banner uploaded" : `Banner set for this message`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading("");
    }
  };

  const clearBanner = (scope) => async () => {
    setUploading(scope);
    try {
      const { data: res } = await api.delete(`/events/${eventId}/banner`, {
        params: scope === "event" ? {} : { kind: scope },
      });
      setBanners(res.banners);
      toast.success("Banner removed");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setUploading("");
    }
  };

  useEffect(() => {
    api
      .get(`/events/${eventId}/messages`)
      .then(({ data }) => {
        setData(data);
        setMessages(data.messages);
        setCustoms(data.custom_messages || []);
        setQuestions(data.survey_questions);
        setBanners(data.banners || {});
        setRsvpPage(data.rsvp_page || {});
        setPassPage(data.pass_page || {});
        if (data.followups) setFollowups(data.followups);
      })
      .catch((e) => toast.error(errText(e)));
  }, [eventId]);

  const setField = (field) => (e) =>
    setMessages({ ...messages, [kind]: { ...messages[kind], [field]: e.target.value } });

  const saveMessages = async () => {
    setSaving(true);
    const custom = Object.fromEntries(customs.map((c) => [c.key, messages[c.key] || {}]));
    try {
      const { data: res } = await api.put(`/events/${eventId}/messages`, {
        ...messages,
        custom,
        rsvp_page: rsvpPage,
        pass_page: passPage,
        followups,
      });
      setMessages(res.messages);
      if (res.rsvp_page) setRsvpPage(res.rsvp_page);
      if (res.pass_page) setPassPage(res.pass_page);
      if (res.followups) setFollowups(res.followups);
      if (res.banners) setBanners(res.banners);
      toast.success("Message wording saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const resetKind = () => {
    const defaults = data.defaults[kind] || data.defaults.custom_default;
    setMessages({ ...messages, [kind]: { ...defaults } });
    if (kind === "invite") setRsvpPage({ ...data.rsvp_page_defaults });
    if (kind === "ticket") {
      setPassPage({ ...data.pass_page_defaults });
    }
    toast.info("Default wording restored — save to apply");
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data: res } = await api.post(`/events/${eventId}/messages/${kind}/preview`, {
        to: testTo || undefined,
      });
      toast.success(`Test sent to ${res.sent_to}`);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setTesting(false);
    }
  };

  const saveQuestions = async () => {
    setSaving(true);
    try {
      const { data: res } = await api.put(`/events/${eventId}/survey-questions`, { questions });
      setQuestions(res.survey_questions);
      toast.success("Survey questions saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const updateQuestion = (i, patch) =>
    setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

  const move = (i, delta) => {
    const next = [...questions];
    const target = i + delta;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    setQuestions(next);
  };

  const addQuestion = () =>
    setQuestions([
      ...questions,
      { id: `q_${crypto.randomUUID().slice(0, 8)}`, label: "", type: "stars", required: false },
    ]);

  const addCustom = async () => {
    const name = window.prompt("Name this message (e.g. Parking details)");
    if (!name?.trim()) return;
    try {
      const { data: res } = await api.post(`/events/${eventId}/custom-messages`, { name });
      setCustoms(res.custom_messages);
      setMessages((m) => ({ ...m, [res.key]: res.message }));
      setData((d) => ({ ...d, defaults: { ...d.defaults, [res.key]: res.defaults } }));
      setKind(res.key);
      onCustomChange?.(res.custom_messages);
      toast.success(`"${name.trim()}" added`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const renameCustom = async () => {
    const current = customs.find((c) => c.key === kind);
    const name = window.prompt("Rename this message", current?.name || "");
    if (!name?.trim()) return;
    try {
      const { data: res } = await api.put(`/events/${eventId}/custom-messages/${kind}`, { name });
      setCustoms(res.custom_messages);
      onCustomChange?.(res.custom_messages);
      toast.success("Renamed");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const deleteCustom = async () => {
    const current = customs.find((c) => c.key === kind);
    if (!window.confirm(`Delete "${current?.name}"? Its wording is removed for good.`)) return;
    try {
      const { data: res } = await api.delete(`/events/${eventId}/custom-messages/${kind}`);
      setCustoms(res.custom_messages);
      if (res.banners) setBanners(res.banners);
      setKind("invite");
      onCustomChange?.(res.custom_messages);
      toast.success("Message deleted");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  if (!data) return <p className="text-sm text-slate-500 mt-6">Loading message settings…</p>;

  const msg = messages[kind] || {};
  const programme = msg.programme || [];
  const setProgrammeRows = (rows) =>
    setMessages((m) => ({ ...m, [kind]: { ...(m[kind] || {}), programme: rows } }));
  const programmeHtml = () =>
    programme.filter((r) => r.time || r.item).length === 0
      ? ""
      : `<table style="width:100%;border-collapse:collapse;margin:12px 0">${programme
          .filter((r) => r.time || r.item)
          .map(
            (r) =>
              `<tr><td style="padding:6px 12px 6px 0;border-bottom:1px solid #e3e8ef;white-space:nowrap;font-weight:700">${escapeHtml(
                r.time
              )}</td><td style="padding:6px 0;border-bottom:1px solid #e3e8ef">${escapeHtml(
                r.item
              )}</td></tr>`
          )
          .join("")}</table>`;
  const extraImage = banners[`${kind}_image`] || "";
  const withImage = (html) => {
    const withProg = (html || "").replaceAll("{programme}", programmeHtml());
    if (!extraImage) return withProg.replaceAll("{image}", "");
    const tag = `<img src="${extraImage}" alt="" style="display:block;max-width:100%;border-radius:10px;margin:12px 0" />`;
    return withProg.includes("{image}") ? withProg.replaceAll("{image}", tag) : withProg + tag;
  };
  const isCustom = customs.some((c) => c.key === kind);
  const tabs = [...KINDS, ...customs.map((c) => [c.key, c.name])];

  return (
    <div className="mt-6 space-y-8" data-testid="messages-tab">
      <div>
        <div className="flex flex-wrap gap-2">
          {tabs.map(([id, label]) => (
            <button
              key={id}
              onClick={() => setKind(id)}
              data-testid={`message-kind-${id}`}
              className={`font-mono2 text-[11px] px-3 py-1.5 rounded-full border transition-colors ${
                kind === id
                  ? "border-[#00e599] text-[#00e599]"
                  : "border-[#38475c] text-slate-400 hover:text-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
          {customs.length < (data.max_custom_messages || 12) && (
            <button
              onClick={addCustom}
              data-testid="add-custom-message-btn"
              className="font-mono2 text-[11px] px-3 py-1.5 rounded-full border border-dashed border-[#38475c] text-slate-400 hover:text-[#00e599] hover:border-[#00e599]/50 transition-colors"
            >
              <Plus size={12} className="inline -mt-0.5 mr-1" /> New message
            </button>
          )}
        </div>

        {isCustom && (
          <div className="flex flex-wrap items-center gap-3 mt-4" data-testid="custom-message-tools">
            <p className="text-[11px] text-slate-500 flex-1 min-w-[240px]">
              Your own message — it carries no RSVP or pass link, so use it for anything you need to
              tell guests. Send it from the attendee row or the bulk actions at the top.
            </p>
            <button onClick={renameCustom} className="btn-ghost text-xs" data-testid="rename-custom-btn">
              <Pencil size={13} /> Rename
            </button>
            <button
              onClick={deleteCustom}
              className="btn-ghost text-xs text-red-400/80 hover:text-red-400"
              data-testid="delete-custom-btn"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mt-5">
          <div className="card p-5">
            <p className="label">Email subject</p>
            <input className="input" data-testid="message-subject-input" value={msg.subject || ""} onChange={setField("subject")} />
            <p className="label mt-4">Heading</p>
            <input className="input" data-testid="message-heading-input" value={msg.heading || ""} onChange={setField("heading")} />
            <p className="label mt-4">Body</p>
            <RichText value={msg.body || ""} onChange={(html) => setMessages((m) => ({ ...m, [kind]: { ...m[kind], body: html } }))} testid="message-body-input" />
            <p className="text-[11px] text-slate-500 mt-2" data-testid="qr-placeholder-help">
              Type <span className="font-mono2 text-[#00e599]">{"{qr}"}</span> anywhere in the body —
              just like {"{first_name}"} — and that guest's own QR pass appears there, also attached
              as a PNG in case their email blocks images. It is left out for anyone whose pass is not
              valid yet.
            </p>
            <p className="text-[11px] text-slate-500 mt-2" data-testid="image-placeholder-help">
              Type <span className="font-mono2 text-[#00e599]">{"{image}"}</span> to place the extra
              image (uploaded below) exactly there.
            </p>
            <p className="text-[11px] text-slate-500 mt-1" data-testid="table-placeholder-help">
              <span className="font-mono2 text-[#00e599]">{"{table}"}</span> fills in the guest's
              table from the Attendees page, and is blank for anyone without one.
            </p>
            <p className="text-[11px] text-slate-500 mt-1" data-testid="programme-placeholder-help">
              <span className="font-mono2 text-[#00e599]">{"{programme}"}</span> drops in the
              programme flow table you build below.
            </p>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-3">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="message-attach-qr"
                checked={msg.attach_qr === true}
                onChange={(e) =>
                  setMessages((m) => ({ ...m, [kind]: { ...(m[kind] || {}), attach_qr: e.target.checked } }))
                }
              />
              Attach the QR pass as a file, without showing it in the message
            </label>
            <p className="label mt-4">Button label</p>
            <input className="input" data-testid="message-button-input" value={msg.button || ""} onChange={setField("button")} />
            <p className="text-[11px] text-slate-500 mt-1">
              Leave blank to send this email without a button.
            </p>
            <p className="label mt-4">Button goes to</p>
            <select
              className="input"
              data-testid="message-link-target-select"
              value={msg.link_target || ""}
              onChange={setField("link_target")}
            >
              {LINK_TARGETS.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            {msg.link_target === "url" && (
              <input
                className="input mt-2 font-mono2 text-xs"
                placeholder="https://"
                data-testid="message-link-url-input"
                value={msg.link_url || ""}
                onChange={setField("link_url")}
              />
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Every guest gets their own personal link. {"{link}"} in the WhatsApp text goes to the
              same place.
            </p>
            <p className="label mt-4">WhatsApp text</p>
            <textarea rows="3" className="input" data-testid="message-whatsapp-input" value={msg.whatsapp || ""} onChange={setField("whatsapp")} />

            <div className="border-t border-[#28323f] mt-5 pt-4">
              <p className="label">Email banner</p>
              <p className="text-[11px] text-slate-500 mt-1">
                PNG, JPG or WebP up to 2MB. About 1200×400px works best. The event banner is used
                on every email unless a message has its own.
              </p>
              {[["event", "Event banner (all emails)"], [kind, `Override for this message only`]].map(
                ([scope, label]) => (
                  <div key={scope} className="mt-3">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono2 text-[11px] text-slate-400">{label}</span>
                      <div className="flex items-center gap-2">
                        <label
                          className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-300 hover:text-[#00e599] hover:border-[#00e599]/50 cursor-pointer transition-colors"
                          data-testid={`banner-upload-${scope}`}
                        >
                          {uploading === scope ? "Uploading…" : banners[scope] ? "Replace" : "Upload"}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            onChange={uploadBanner(scope)}
                            disabled={!!uploading}
                          />
                        </label>
                        {banners[scope] && (
                          <button
                            onClick={clearBanner(scope)}
                            disabled={!!uploading}
                            className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-400 hover:text-[#ff6b6b]"
                            data-testid={`banner-clear-${scope}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                    {banners[scope] ? (
                      <img
                        src={banners[scope]}
                        alt=""
                        className="mt-2 w-full rounded-lg border border-[#28323f]"
                        data-testid={`banner-thumb-${scope}`}
                      />
                    ) : (
                      <p className="font-mono2 text-[10px] text-slate-600 mt-1">none</p>
                    )}
                  </div>
                )
              )}
            </div>

            <div className="border-t border-[#28323f] mt-5 pt-4">
              <div className="flex items-center justify-between gap-3">
                <p className="label mb-0">Programme flow</p>
                <button
                  onClick={() => setProgrammeRows([...programme, { time: "", item: "" }])}
                  className="btn-ghost text-xs"
                  data-testid="programme-add-row"
                >
                  <Plus size={13} /> Add row
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Type <span className="font-mono2 text-[#00e599]">{"{programme}"}</span> in the body
                to place this table. Leave it empty and nothing is added.
              </p>
              {programme.length > 0 && (
                <div className="mt-3 space-y-2" data-testid="programme-rows">
                  {programme.map((row, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <div className="w-28 shrink-0">
                        <input
                          className="input"
                          placeholder="7:00 PM"
                          maxLength={40}
                          data-testid={`programme-time-${i}`}
                          value={row.time || ""}
                          onChange={(e) =>
                            setProgrammeRows(
                              programme.map((x, j) => (j === i ? { ...x, time: e.target.value } : x))
                            )
                          }
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <input
                          className="input"
                          placeholder="Guests seated, welcome remarks"
                          maxLength={160}
                          data-testid={`programme-item-${i}`}
                          value={row.item || ""}
                          onChange={(e) =>
                            setProgrammeRows(
                              programme.map((x, j) => (j === i ? { ...x, item: e.target.value } : x))
                            )
                          }
                        />
                      </div>
                      <button
                        onClick={() => setProgrammeRows(programme.filter((_, j) => j !== i))}
                        className="btn-ghost px-2 text-slate-500 hover:text-[#ff6b6b]"
                        data-testid={`programme-remove-${i}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-[#28323f] mt-5 pt-4">
              <p className="label">Extra image (e.g. carpark map)</p>
              <p className="text-[11px] text-slate-500 mt-1">
                Shows inside the body of this message only. Type{" "}
                <span className="font-mono2 text-[#00e599]">{"{image}"}</span> where you want it —
                leave it out and the picture goes at the end of the body.
              </p>
              <div className="mt-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono2 text-[11px] text-slate-400">This message's image</span>
                  <div className="flex items-center gap-2">
                    <label
                      className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-300 hover:text-[#00e599] hover:border-[#00e599]/50 cursor-pointer transition-colors"
                      data-testid={`image-upload-${kind}`}
                    >
                      {uploading === `${kind}_image`
                        ? "Uploading…"
                        : banners[`${kind}_image`]
                        ? "Replace"
                        : "Upload"}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={uploadBanner(`${kind}_image`)}
                        disabled={!!uploading}
                      />
                    </label>
                    {banners[`${kind}_image`] && (
                      <button
                        onClick={clearBanner(`${kind}_image`)}
                        disabled={!!uploading}
                        className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-400 hover:text-[#ff6b6b]"
                        data-testid={`image-clear-${kind}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
                {banners[`${kind}_image`] ? (
                  <>
                    <img
                      src={banners[`${kind}_image`]}
                      alt=""
                      className="mt-2 w-full rounded-lg border border-[#28323f]"
                      data-testid={`image-thumb-${kind}`}
                    />
                    <label className="flex items-center gap-2 text-xs text-slate-300 mt-3">
                      <input
                        type="checkbox"
                        className="accent-[#00e599] w-4 h-4"
                        data-testid="message-attach-image"
                        checked={msg.attach_image !== false}
                        onChange={(e) =>
                          setMessages((m) => ({
                            ...m,
                            [kind]: { ...m[kind], attach_image: e.target.checked },
                          }))
                        }
                      />
                      Also attach it as a file, for inboxes that block images
                    </label>
                  </>
                ) : (
                  <p className="font-mono2 text-[10px] text-slate-600 mt-1">none</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {data.placeholders.map((p) => (
                <button
                  key={p}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(p);
                      toast.success(`${p} copied`);
                    } catch {
                      toast.info(`Copy manually: ${p}`);
                    }
                  }}
                  className="font-mono2 text-[10px] px-2 py-1 rounded-md border border-[#38475c] text-slate-400 hover:text-[#00e599]"
                  title="Click to copy"
                  data-testid={`placeholder-copy-${p.replace(/[{}]/g, "")}`}
                >
                  {p}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-5">
              <button onClick={saveMessages} disabled={saving} className="btn-primary" data-testid="save-messages-btn">
                <Save size={15} /> {saving ? "Saving…" : "Save wording"}
              </button>
              <input
                className="input max-w-[220px]"
                placeholder="Test email (optional)"
                data-testid="test-email-input"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <button onClick={sendTest} disabled={testing} className="btn-secondary" data-testid="send-test-message-btn">
                <Send size={15} /> {testing ? "Sending…" : "Send test to myself"}
              </button>
              <button onClick={resetKind} className="btn-ghost text-xs" data-testid="reset-message-btn">
                <RotateCcw size={13} /> Restore default
              </button>
            </div>
            <p className="font-mono2 text-[10px] text-slate-500 mt-3" data-testid="cc-status">
              {data.cc_active
                ? `Every email on this event is CC'd to ${data.cc_email}`
                : !data.cc_email
                ? "No organiser CC set — add one in Edit details"
                : !data.cc_valid
                ? `CC is off — "${data.cc_email}" is not a valid email address`
                : `CC to ${data.cc_email} is set but inactive — CC needs your own verified sending domain`}
            </p>
          </div>

          <div>
            <p className="label">Live preview</p>
            <div className="bg-white text-slate-900 rounded-xl p-6 shadow-2xl" data-testid="message-preview">
              <p className="text-[11px] text-slate-500 border-b border-slate-200 pb-2 mb-4">
                <span className="font-semibold">Subject:</span> {fill(msg.subject, event)}
              </p>
              {(banners[kind] || banners.event) && (
                <img
                  src={banners[kind] || banners.event}
                  alt=""
                  className="w-full rounded-lg mb-4"
                  data-testid="preview-banner"
                />
              )}
              <h3 className="text-xl font-bold">{fill(msg.heading, event)}</h3>
              {isRich(msg.body) ? (
                <div
                  className="text-sm leading-relaxed mt-3 text-slate-700 [&_a]:text-slate-900 [&_a]:underline"
                  data-testid="preview-body"
                  dangerouslySetInnerHTML={{ __html: withImage(flatten(fill(msg.body, event))) }}
                />
              ) : (
                <div
                  className="text-sm leading-relaxed mt-3 whitespace-pre-line text-slate-700"
                  data-testid="preview-body"
                  dangerouslySetInnerHTML={{ __html: withImage(escapeHtml(fill(msg.body, event))) }}
                />
              )}
              {(msg.button || "").trim() && (
                <p className="mt-5">
                  <span className="inline-block bg-slate-900 text-white text-sm font-bold px-5 py-3 rounded-lg">
                    {fill(msg.button, event)}
                  </span>
                </p>
              )}
            </div>
            <div className="card p-4 mt-4">
              <p className="label">WhatsApp preview</p>
              <p className="text-sm text-slate-200 bg-[#141a23] rounded-lg p-3 whitespace-pre-line" data-testid="whatsapp-preview">
                {fill(msg.whatsapp, event) || "—"}
              </p>
              {!data.whatsapp?.enabled && (
                <p className="text-[11px] text-[#ffb800] mt-2">WhatsApp is inactive — this text is not being sent yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div>
        {kind === "ticket" && (
          <div className="card p-5 mt-6" data-testid="pass-page-editor">
            <p className="label">Pass page wording</p>
            <p className="text-[11px] text-slate-500 mt-1">
              What a guest sees on the page their QR pass link opens. Rename anything — for example
              call the pass a "Digital invite" and your guests "Esteemed guest".
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                ["status_label", "Status line (pass ready)"],
                ["person_label", "Guest label"],
                ["held_label", "Status line (pass not released)"],
                ["waitlist_label", "Status line (waitlisted)"],
                ["badge_heading", "Preview heading"],
                ["resend_label", "Resend button"],
                ["print_label", "Print button"],
                ["survey_label", "Feedback link"],
                ["held_note", "Note while the pass is held"],
                ["needs_rsvp_note", "Note when they still have to reply"],
                ["waitlist_note", "Note for waitlisted guests"],
                ["privacy_note", "Privacy note at the bottom"],
                ["notes_heading", "Before-you-arrive heading"],
                ["dress_code", "Dress code"],
                ["arrival_time", "Arrival time"],
                ["parking", "Parking"],
                ["notes", "Anything else guests should know"],
              ].map(([key, label]) => (
                <div key={key} className={["held_note", "needs_rsvp_note", "waitlist_note", "privacy_note", "notes"].includes(key) ? "sm:col-span-2" : ""}>
                  <label className="label">{label}</label>
                  {key === "notes" ? (
                    <textarea
                      className="input min-h-[80px]"
                      rows={3}
                      data-testid="pass-page-notes-input"
                      placeholder="One line per note — shows on the confirmed pass only"
                      value={passPage.notes || ""}
                      onChange={(e) => setPassPage({ ...passPage, notes: e.target.value })}
                    />
                  ) : (
                    <input
                      className="input"
                      data-testid={`pass-page-${key.replaceAll("_", "-")}-input`}
                      value={passPage[key] || ""}
                      onChange={(e) => setPassPage({ ...passPage, [key]: e.target.value })}
                    />
                  )}
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-4">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="pass-page-show-badge"
                checked={passPage.show_badge !== false}
                onChange={(e) => setPassPage({ ...passPage, show_badge: e.target.checked })}
              />
              Show the preview of their badge on this page
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-2">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="pass-page-show-survey"
                checked={passPage.show_survey === true}
                onChange={(e) => setPassPage({ ...passPage, show_survey: e.target.checked })}
              />
              Show the post-event feedback link on this page
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-2">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="pass-page-show-dietary"
                checked={passPage.show_dietary === true}
                onChange={(e) => setPassPage({ ...passPage, show_dietary: e.target.checked })}
              />
              Let guests edit their dietary needs on this page
            </label>
            <p className="font-mono2 text-[10px] text-slate-500 mt-3">
              Saved with "Save wording" above. Blank fields fall back to the standard text.
            </p>
          </div>
        )}

        {kind === "invite" && (
          <div className="card p-5 mt-6" data-testid="rsvp-page-editor">
            <p className="label">RSVP reply page</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Everything a guest sees on the page your invitation links to. Placeholders like{" "}
              <span className="font-mono2">{"{first_name}"}</span> work here too.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              {[
                ["intro", "Intro line"],
                ["question", "Question label"],
                ["yes_label", "Yes button"],
                ["no_label", "No button"],
                ["answered_yes", "After they reply yes"],
                ["answered_no", "After they reply no"],
                ["pass_pending", "Waiting-for-pass note"],
                ["thanks_yes", "Thank-you note after a yes"],
                ["dietary_label", "Dietary question"],
                ["dietary_hint", "Dietary hint"],
                ["pass_link_label", "QR pass button label"],
                ["footer", "Footer note"],
              ].map(([key, label]) => (
                <div key={key} className={["intro", "footer", "pass_pending", "dietary_hint", "thanks_yes"].includes(key) ? "sm:col-span-2" : ""}>
                  <label className="label">{label}</label>
                  <input
                    className="input"
                    data-testid={`rsvp-page-${key.replaceAll("_", "-")}-input`}
                    value={rsvpPage[key] || ""}
                    onChange={(e) => setRsvpPage({ ...rsvpPage, [key]: e.target.value })}
                  />
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-4">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="rsvp-page-show-pass-link"
                checked={rsvpPage.show_pass_link !== false}
                onChange={(e) => setRsvpPage({ ...rsvpPage, show_pass_link: e.target.checked })}
              />
              Show the QR pass button after they reply yes
            </label>
            <div className="mt-5 bg-[#141a23] rounded-xl p-5" data-testid="rsvp-page-preview">
              <p className="text-sm text-slate-300">{fill(rsvpPage.intro, event)}</p>
              <p className="label mt-4">{fill(rsvpPage.question, event)}</p>
              <div className="flex flex-wrap gap-3 mt-2">
                <span className="btn-primary pointer-events-none">{fill(rsvpPage.yes_label, event)}</span>
                <span className="btn-secondary pointer-events-none">{fill(rsvpPage.no_label, event)}</span>
              </div>
              <p className="font-mono2 text-[11px] text-slate-500 mt-4">
                {fill(rsvpPage.answered_yes, event)} · {fill(rsvpPage.answered_no, event)}
              </p>
              <p className="text-sm text-slate-300 mt-3">{fill(rsvpPage.thanks_yes, event)}</p>
              <p className="text-[11px] text-slate-500 mt-2">{fill(rsvpPage.pass_pending, event)}</p>
              <p className="text-[11px] text-slate-500 mt-2">{fill(rsvpPage.footer, event)}</p>
            </div>
            <p className="font-mono2 text-[10px] text-slate-500 mt-3">
              Saved with "Save wording" above. "Restore default" on this tab resets these too.
            </p>
          </div>
        )}

        {kind === "invite" && (
          <div className="card p-5 mt-6" data-testid="followup-editor">
            <p className="label">Automatic follow-up</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Off by default. When on, replying yes sends the message you pick below — once per
              guest. Everything else still goes out only when you send it.
            </p>
            <label className="flex items-center gap-2 text-xs text-slate-300 mt-4">
              <input
                type="checkbox"
                className="accent-[#00e599] w-4 h-4"
                data-testid="followup-rsvp-yes-toggle"
                checked={!!followups.rsvp_yes_enabled}
                onChange={(e) => setFollowups({ ...followups, rsvp_yes_enabled: e.target.checked })}
              />
              Email a guest automatically when they reply yes
            </label>
            <div className="mt-3 max-w-xs">
              <p className="label">Message to send</p>
              <select
                className="input"
                data-testid="followup-rsvp-yes-kind"
                disabled={!followups.rsvp_yes_enabled}
                value={followups.rsvp_yes_kind || "ticket"}
                onChange={(e) => setFollowups({ ...followups, rsvp_yes_kind: e.target.value })}
              >
                {KINDS.map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
            </div>
            <p className="font-mono2 text-[10px] text-slate-500 mt-3">
              Saved with "Save wording" above. Follow-ups count towards your daily email limit.
            </p>
          </div>
        )}
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#252f3e] pt-6">
          <div>
            <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight">Survey questions</h3>
            <p className="text-xs text-slate-400 mt-1">
              Star ratings, yes/no and free-text questions. Attendees see them in this order.
            </p>
          </div>
          <div className="flex gap-3">
            <button onClick={addQuestion} className="btn-secondary" data-testid="add-question-btn">
              <Plus size={15} /> Add question
            </button>
            <button onClick={saveQuestions} disabled={saving} className="btn-primary" data-testid="save-questions-btn">
              <Save size={15} /> Save questions
            </button>
          </div>
        </div>

        <div className="space-y-3 mt-5">
          {questions.map((q, i) => (
            <div key={q.id} className="card p-4 flex flex-wrap items-center gap-3" data-testid={`question-row-${i}`}>
              <input
                className="input flex-1 min-w-[220px]"
                placeholder="Question label"
                data-testid={`question-label-${i}`}
                value={q.label}
                onChange={(e) => updateQuestion(i, { label: e.target.value })}
              />
              <select
                className="input w-36"
                data-testid={`question-type-${i}`}
                value={q.type}
                onChange={(e) => updateQuestion(i, { type: e.target.value })}
              >
                <option value="stars">Star rating</option>
                <option value="yesno">Yes / No</option>
                <option value="text">Free text</option>
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input
                  type="checkbox"
                  className="accent-[#00e599] w-4 h-4"
                  data-testid={`question-required-${i}`}
                  checked={q.required}
                  onChange={(e) => updateQuestion(i, { required: e.target.checked })}
                />
                Required
              </label>
              <div className="flex gap-1 ml-auto">
                <button onClick={() => move(i, -1)} className="btn-ghost p-1" data-testid={`question-up-${i}`}>
                  <ArrowUp size={14} />
                </button>
                <button onClick={() => move(i, 1)} className="btn-ghost p-1" data-testid={`question-down-${i}`}>
                  <ArrowDown size={14} />
                </button>
                <button
                  onClick={() => setQuestions(questions.filter((_, idx) => idx !== i))}
                  className="text-red-400/70 hover:text-red-400 p-1"
                  data-testid={`question-delete-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
